import {
  type DocumentChunk,
  type DocumentChunkManifest,
  type DocumentChunkManifestEntry,
  type DocumentChunkMap,
  type DocumentChunkMapByFilename,
  DOCUMENT_CHUNK_TYPES_TEXT, UNCHUNKED_DOCUMENT_CHUNK_ID,
} from '../types/chunkedDocuments';
import { notEmpty } from '../typeValidators';
// eslint-disable-next-line import/no-cycle
import { ensureBookwiseLicenseTokenForDocId } from '../utils/bookwiseLicenseTokens.platform';
// eslint-disable-next-line import/no-cycle
import { forceReRegister } from '../utils/deviceKeyRegistration.platform';
import { isBookwise } from '../utils/env.platform';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../utils/exceptionHandler.platform';
import makeLogger from '../utils/makeLogger';
import type { CacheInstance } from './Cache';
import { loadWithExponentialBackoff } from './exponentialBackoffLoader';
import { FastCache } from './FastCache.platform';
import { createManifestLoadingTracker } from './manifestLoadingTracker';
import foreground from './portalGates/toForeground';
import { FileBackedStore } from './stores/FileBackedStore.platform';
import { DeviceNotRegisteredError } from './stores/ItemFetcher';

// same as MAX_DOCUMENT_CHUNKS_COUNT_PER_PAGE in reader/constants.py
const MAX_DOCUMENT_CHUNKS_COUNT_PER_PAGE = 30;
const MIN_TIME_BETWEEN_LOADS_FROM_SERVER = 3000; // millis

let isLoadingFromServer = false;
let isAppActive = true;

const chunkFileStore = new FileBackedStore<DocumentChunk>({
  name: 'documentContentChunks',
  endpointPath: 'documents/chunks',
  onItemsLoaded: (chunkMap) => documentProgressTracker.handleItemsLoaded(chunkMap),
});

const queuedManifestsCache: CacheInstance = FastCache.createInstance({
  name: 'queuedManifestsToLoad',
});

const logger = makeLogger(__filename, { shouldLogToNewRelic: true });

const documentProgressTracker = createManifestLoadingTracker<DocumentChunkManifestEntry, DocumentChunk>({
  eventName: 'documentContentChunksLoaded',
  itemName: 'chunk',
});

async function getChunkRequestExtraQueryParams(docId?: string): Promise<URLSearchParams | undefined> {
  if (!isBookwise || !docId) {
    return undefined;
  }
  const licenseToken = await ensureBookwiseLicenseTokenForDocId(docId);
  if (!licenseToken) {
    // No token available — still allow the request. Server-side enforcement
    // will gate DRM-protected content; non-DRM content loads without a token.
    return undefined;
  }
  const params = new URLSearchParams();
  params.set('bookwise_license_token', licenseToken);
  return params;
}

export function updateDocumentChunkLoadingActiveStatus(newIsActive: boolean) {
  isAppActive = newIsActive;
  if (isAppActive && !isLoadingFromServer) {
    loadDocumentChunksInBackground();
  }
}

export function loadDocumentChunksInBackground() {
  if (isBookwise) {
    // Experiment to see if disabling chunk downloading in BG improves app feel
    return;
  }
  if (isLoadingFromServer) {
    logger.warn('Already loading chunks in background, bailing.');
    return;
  }
  logger.info('Starting process to load chunks from server');
  isLoadingFromServer = true;
  loadChunkBatchFromServer();
}

export async function clearCache() {
  logger.debug('Clearing cached chunks and manifests queued for load');
  await Promise.all([
    queuedManifestsCache.clear(),
    chunkFileStore.clearCache(),
  ]);
}

export async function forceRedownloadChunksWithIds(ids: string[], docId?: string): Promise<{ [dbId: string]: DocumentChunk; }> {
  await chunkFileStore.removeByIds(ids);
  return loadWithExponentialBackoff({
    requestedIds: ids,
    load: async (chunkIds, isRetry) => {
      const extraQueryParams = await getChunkRequestExtraQueryParams(docId);
      return chunkFileStore.loadByIds(chunkIds, isRetry, extraQueryParams);
    },
    isItemValid: (chunk) => chunk.data !== '',
  });
}

export async function removeQueuedManifestsForDocIds(docIds: string[]): Promise<void> {
  if (docIds.length === 0) {
    return;
  }
  await queuedManifestsCache.removeItems(docIds);
}

export async function removeChunksByIds(chunkIds: string[]): Promise<void> {
  if (chunkIds.length === 0) {
    return;
  }
  await chunkFileStore.removeByIds(chunkIds);
}

export async function queueLoadForChunksFromManifest(docId: string, manifest: DocumentChunkManifest) {
  const startTime = performance.now();
  await queuedManifestsCache.setItem(docId, manifest);
  logger.debug(`Queueing manifest took ${(performance.now() - startTime).toFixed(2)}ms`, {
    docId,
    chunkCount: Object.keys(manifest).length,
  });
}

/**
 * We don't want to overload the client and degrade performance, so we throttle the chunk requests.
 * This will combine and request chunks from multiple documents at the same time.
 */
async function loadChunkBatchFromServer() {
  const docIdsInQueue = await queuedManifestsCache.keys();
  // sort doc IDs in queue from most recently created to least recently created.
  // this means doc manifests will not be loaded in the order they are queued, which makes this process technically
  // not FIFO. but queueing by doc ID is a much simpler and safer implementation than writing and reading an array.
  docIdsInQueue.sort().reverse();

  const chunkIdBatch: string[] = [];
  const docIdsInBatch: string[] = [];
  const chunkIdsByDocId: { [docId: string]: string[]; } = {};
  while (docIdsInQueue.length > 0) {
    if (!isAppActive) {
      isLoadingFromServer = false;
      break;
    }
    const docId = docIdsInQueue.shift(); // get the first one i.e. the most recently created doc.
    if (!docId) {
      logger.error('>0 doc IDs in queue but pop() returns nullish', { docId, docIdsInQueue });
      return;
    }
    const manifest = await queuedManifestsCache.getItem<DocumentChunkManifest>(docId);
    if (!manifest) {
      logger.warn('Queued manifest was already popped, skipping..', {
        docId,
      });
      continue;
    }
    // load ALL chunks from manifest
    const chunkIdsToAdd = extractChunkIdsFromManifestEntries(Object.values(manifest));
    chunkIdBatch.push(...chunkIdsToAdd);
    docIdsInBatch.push(docId);
    chunkIdsByDocId[docId] = chunkIdsToAdd;
    logger.debug('Added chunk IDs to batch for loading', {
      docId,
      chunkIdsToAdd,
      chunkIdBatch,
    });
    if (chunkIdBatch.length >= MAX_DOCUMENT_CHUNKS_COUNT_PER_PAGE) {
      break;
    }
  }

  foreground.updateChunkedDocDebugInfo(docIdsInQueue, docIdsInBatch, chunkIdBatch);
  if (chunkIdBatch.length > 0) {
    logger.debug('Loading chunk batch from server', {
      chunkIdBatch,
      docIdsInBatch,
    });
    try {
      if (isBookwise) {
        // Bookwise license tokens are per-document, so we must split loads by doc ID.
        for (const docId of docIdsInBatch) {
          const chunkIdsForDoc = chunkIdsByDocId[docId] ?? [];
          if (chunkIdsForDoc.length === 0) {
            continue;
          }
          await loadWithExponentialBackoff({
            requestedIds: chunkIdsForDoc,
            load: async (ids, isRetry) => {
              const extraQueryParams = await getChunkRequestExtraQueryParams(docId);
              return chunkFileStore.loadByIds(ids, isRetry, extraQueryParams);
            },
            isItemValid: (chunk) => chunk.data !== '',
            progressTracker: documentProgressTracker,
          });
        }
      } else {
        await loadWithExponentialBackoff({
          requestedIds: chunkIdBatch,
          load: (ids, isRetry) => chunkFileStore.loadByIds(ids, isRetry),
          isItemValid: (chunk) => chunk.data !== '',
          progressTracker: documentProgressTracker,
        });
      }
      // only remove doc IDs from queue once we know loading their chunks was successful.
      await Promise.all(docIdsInBatch.map((docId) => queuedManifestsCache.removeItem(docId)));
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : e;
      logger.error('Error loading chunks from server', { errorMessage, chunkIdBatch, docIdsInBatch });
      if (e instanceof Error) {
        throw e;
      } else {
        throw new Error(`unknown error: ${e}`);
      }
    }
  }

  if (isAppActive) {
    setTimeout(loadChunkBatchFromServer, MIN_TIME_BETWEEN_LOADS_FROM_SERVER);
  } else {
    isLoadingFromServer = false;
  }
}


function listenForChunkLoadingProgress(
  docId: string,
  entriesToListenFor: DocumentChunkManifestEntry[],
) {
  documentProgressTracker.listenForEntries(docId, entriesToListenFor);
}


function extractChunkIdsFromManifestEntries(entries: DocumentChunkManifestEntry[]): string[] {
  // For a consistent order of IDs on the client we sort them.
  const manifestChunkIds = entries
    .map((e) => e.id)
    .sort();
  if (!manifestChunkIds.every(notEmpty)) {
    exceptionHandler.captureException(
      'Manifest entries to load contains some empty or nullish IDs',
      {
        extra: {
          manifestChunkIds,
          entries,
        },
      },
    );
    return [];
  }
  if (manifestChunkIds.length === 0) {
    exceptionHandler.captureException(
      'No manifest entries to load',
      {
        extra: {
          manifestChunkIds,
          entries,
        },
      },
    );
    return [];
  }
  return manifestChunkIds;
}

function transformToChunkMapByInternalId(chunkMapByDbId: { [dbId: string]: DocumentChunk; }): DocumentChunkMap {
  return Object.fromEntries(
    Object.values(chunkMapByDbId).map((chunk) => [chunk.internal_id, chunk]),
  );
}

async function loadChunkMapFromManifestEntries(
  manifestEntriesToLoad: DocumentChunkManifestEntry[],
  docId: string,
  hasRetriedAfterReRegister = false,
): Promise<DocumentChunkMap> {
  const manifestChunkIds = extractChunkIdsFromManifestEntries(manifestEntriesToLoad);
  logger.debug('Loading chunks from manifest', {
    manifestChunkIds,
    manifestEntriesToLoad,
    firstChunkId: manifestChunkIds[0],
    lastChunkId: manifestChunkIds[manifestChunkIds.length - 1],
  });

  try {
    const chunkMapByDbId = await loadWithExponentialBackoff<DocumentChunk>({
      requestedIds: manifestChunkIds,
      load: async (ids, isRetry) => {
        // License token (if available) is attached as a query param; server-side
        // enforcement rejects requests for DRM-limited content without a valid token.
        const extraQueryParams = await getChunkRequestExtraQueryParams(docId);
        return chunkFileStore.loadByIds(ids, isRetry, extraQueryParams);
      },
      isItemValid: (chunk) => chunk.data !== '',
    });
    const chunkMap = transformToChunkMapByInternalId(chunkMapByDbId);
    logger.debug('Loaded chunk map', { internalIds: Object.keys(chunkMap) });
    return chunkMap;
  } catch (error) {
    if (error instanceof DeviceNotRegisteredError && !hasRetriedAfterReRegister) {
      const profileId = await foreground.getProfileId();
      if (profileId) {
        logger.info('Device not registered during chunk loading, re-registering and retrying', { profileId });
        await forceReRegister(profileId);
        return loadChunkMapFromManifestEntries(manifestEntriesToLoad, docId, true);
      }
    }
    throw error;
  }
}

async function loadChunksByInternalIdFromManifest(
  manifest: DocumentChunkManifest,
  internalIdsToLoad: string[],
  docId: string,
): Promise<DocumentChunkMap> {
  const manifestEntriesToLoad = internalIdsToLoad
    .map((internalId) => manifest[internalId])
    .filter(notEmpty);
  const missingManifestEntries = internalIdsToLoad.filter((internalId) => !manifest[internalId]);
  if (missingManifestEntries.length > 0) {
    exceptionHandler.captureException('Missing manifest entries for chunk internal IDs', {
      extra: {
        missingManifestEntries,
        allManifestKeys: Object.keys(manifest),
        internalIdsToLoad,
        manifestEntriesToLoad,
      },
    });
  }
  if (manifestEntriesToLoad.length === 0) {
    exceptionHandler.captureException('No manifest entries to load?', {
      extra: {
        missingManifestEntries,
        allManifestKeys: Object.keys(manifest),
        internalIdsToLoad,
        manifestEntriesToLoad,
      },
    });
    return {};
  }
  return loadChunkMapFromManifestEntries(manifestEntriesToLoad, docId);
}

function extractTextChunkEntries(manifest: DocumentChunkManifest) {
  return Object
    .values(manifest)
    .filter((e) => DOCUMENT_CHUNK_TYPES_TEXT.has(e.type));
}

export function loadAllTextChunksFromManifest(
  manifest: DocumentChunkManifest,
  docId: string, // pass this if you want to listen for chunk loading progress
): Promise<DocumentChunkMap> {
  const textChunkManifestEntries = extractTextChunkEntries(manifest);
  listenForChunkLoadingProgress(docId, textChunkManifestEntries);
  return loadChunkMapFromManifestEntries(textChunkManifestEntries, docId);
}

export async function loadChunksByFilenameFromManifest(
  manifest: DocumentChunkManifest,
  absoluteFilepaths: string[],
  docId: string,
): Promise<DocumentChunkMapByFilename> {
  const filenameToInternalIdMap = Object.fromEntries(
    Object.entries(manifest).map(([internalId, entry]) => [entry.filename, internalId]),
  );
  const internalIdsToLoad = absoluteFilepaths
    .map((filepath) => filenameToInternalIdMap[filepath])
    .filter(notEmpty);
  if (internalIdsToLoad.length === 0) {
    return {};
  }
  const chunkMap = await loadChunksByInternalIdFromManifest(manifest, internalIdsToLoad, docId);
  return Object.fromEntries(
    Object.values(chunkMap).map((chunk) => [chunk.filename, chunk]),
  );
}

export async function loadChunksFromSpineAndManifest(sortedSpineIds: string[], manifest: DocumentChunkManifest | undefined, docId: string): Promise<DocumentChunk[] | null> {
  if (!manifest) {
    exceptionHandler.captureException(
      `No manifest found for doc`,
      {
        extra: {
          sortedSpineIds,
          manifest,
          docId,
        },
      },
    );
    return null;
  }
  if (sortedSpineIds[0] === UNCHUNKED_DOCUMENT_CHUNK_ID) {
    exceptionHandler.captureException(
      `Chunked document failed to provide a spine, (ONLY UNCHUNKED DOCUMENT CHUNK ID PROVIDED)`,
      {
        extra: {
          sortedSpineIds,
          manifest,
          docId,
        },
      },
    );
  }
  const chunkMap = await loadAllTextChunksFromManifest(manifest, docId);
  const chunks = sortedSpineIds.map((id) => chunkMap[id]);
  if (!chunks.every(notEmpty)) {
    return null;
  }
  return chunks;
}
